import { createSelector } from 'reselect';

import { emptyArray, emptyObject } from 'Lib/constants/placeholder';
import { GlobalStateType } from 'Lib/reduxType';
import { createKeySelector } from 'Lib/reduxUtils';
import { calculateAge } from 'Lib/utils/dateTime';

import { STATE_LOADING } from 'Store/commonActions';
import { PRODUCT_TYPE_GOLD } from 'Store/entities/products.schema';
import { GENDER_MALE, genderIsFemale, genderIsMale } from 'Store/entities/profiles.schema';
import {
  BOOST_TUTORIAL,
  FAST_MATCH_INTRO,
  MUTUAL_FRIENDS_TUTORIAL,
  SELECTOR_TYPE_CHOICE_SELECTOR_V1,
  SELECTOR_TYPE_MULTI_SELECTION_SET,
  SELECTOR_TYPE_SINGLE_SELECTION_SET,
} from 'Store/profile/constants';
import type { OnboardingTutorial, ProfileState } from 'Store/profile/types';

import type { Descriptor, User, UserQuiz, UserQuizId, UserQuizzesSection } from './user.types';
import { formatDescriptorsById } from './utils';

/**
 * Gets profile namespace from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {ProfileState} profile state
 */
export const selectProfile = createKeySelector('profile');

/**
 * Indicates if tinder account of the current user is connected to google account
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} if google id is linked to tinder account
 */
export const getGoogleIdLinked = createSelector(
  selectProfile,
  (profile) => profile.account?.google_id_linked
);

/**
 * Indicates if tinder account of the current user is connected to line account
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} if line id is linked to tinder account
 */
export const getLineIdLinked = createSelector(
  selectProfile,
  (profile) => profile.account?.line_id_linked
);

/**
 * Indicates if tinder account of the current user is connected to facebook account
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} if facebook id is linked to tinder account
 */
export const getFacebookIdLinked = createSelector(
  selectProfile,
  (profile) => profile.account?.facebook_id_linked
);

/**
 * Indicates if tinder account of the current user is connected to apple account
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} if apple id is linked to tinder account
 */
export const getAppleIdLinked = createSelector(
  selectProfile,
  (profile) => profile.account?.apple_id_linked
);

/**
 * Gets the latest email of the current user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} user email
 */
export const getEmail = createSelector(
  selectProfile,
  (profile) => profile.account?.new_account_email || profile.account?.account_email || ''
);

/**
 * Gets the verified email of the current user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} user email
 */
export const getVerifiedEmail = createSelector(selectProfile, (profile) =>
  profile.account?.is_email_verified ? profile.account?.account_email : ''
);

/**
 * Gets Contact Cards - both available and populated
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {ContactCards} An Object consisting of populatedCards and availableCards
 */
export const getContactCards = createSelector(selectProfile, (profile) => profile.contactCards);

/**
 * Get populated contact cards
 *
 * @param  {ContactCards} An Object consisting of populatedCards and availableCards
 * @return {Array<ContactCard>} Array of populated contact cards
 */
export const getPopulatedCards = createSelector(
  getContactCards,
  (contactCards) => contactCards.populatedCards ?? emptyArray
);

/**
 * Gets available contact cards (not provided by user)
 *
 * @param  {ContactCards} An Object consisting of populatedCards and availableCards
 * @return {Array<ContactCard>} Array of available cards for the user to populate
 */
export const getUnpopulatedCards = createSelector(getContactCards, (contactCards) => {
  const { availableCards, populatedCards } = contactCards;

  return (
    availableCards
      ?.filter((item) => !populatedCards?.find((pop) => pop.contact_type === item))
      .map((item) => ({
        contact_type: item,
      })) ?? emptyArray
  );
});

/**
 * Gets success flag if updating/adding was successful
 *
 * @param  {ContactCards} An Object consisting of populatedCards and availableCards
 * @return {boolean} If updating was a success
 */
export const getUpdateContactCardSuccess = createSelector(
  getContactCards,
  (contactCards) => contactCards?.updateSuccess
);

/**
 * Gets error flag if updating/adding was not successful
 *
 * @param  {ContactCards} An Object consisting of populatedCards and availableCards
 * @return {boolean} If updating was not a success
 */
export const getUpdateContactCardError = createSelector(
  getContactCards,
  (contactCards) => contactCards?.updateError
);

/**
 * Gets the tutorials on the profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Tutorials} tutorials
 */
export const getProfileTutorials = createSelector(selectProfile, (profile) => profile.tutorials);

/**
 * Gets if the fast match intro (gold intro) should be shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} if fast match intro should be shown
 */
export const getShowFastMatchIntro = createSelector(getProfileTutorials, (tutorials) =>
  tutorials?.includes(FAST_MATCH_INTRO)
);

export const getShowFastBoostIntro = createSelector(getProfileTutorials, (tutorials) =>
  tutorials?.includes(BOOST_TUTORIAL)
);

export const getShowMutualFriendsTutorial = createSelector(getProfileTutorials, (tutorials) =>
  tutorials?.includes(MUTUAL_FRIENDS_TUTORIAL)
);

export const getProfileOnboardingTutorials = createSelector(
  selectProfile,
  (profile: ProfileState) => profile.onboarding.tutorials
);

export const getProfileOnboardingMutualFriendsTutorial = createSelector(
  getProfileOnboardingTutorials,
  (onboardingTutorials: OnboardingTutorial[]) =>
    onboardingTutorials.find(({ name }) => name === MUTUAL_FRIENDS_TUTORIAL) ?? emptyObject
);

/**
 * Gets the discoverable party setting
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {DiscoverableParty} whether the user is traveling
 */
export const getDiscoverableParty = createSelector(
  selectProfile,
  (profile) => profile.plusControl?.discoverable_party || 'everyone'
);

/**
 * Gets the hide ads setting
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the user is hiding ads
 */
export const getHideAds = createSelector(
  selectProfile,
  (profile) => profile.plusControl?.hide_ads ?? false
);

/**
 * Gets the hide age setting
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the user is hiding their age
 */
export const getHideAge = createSelector(
  selectProfile,
  (profile) => profile.plusControl?.hide_age ?? false
);

/**
 * Gets the hide distance setting
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the user is hiding their distance
 */
export const getHideDistance = createSelector(
  selectProfile,
  (profile) => profile.plusControl?.hide_distance ?? false
);

/**
 * Gets the blend setting
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the user is hiding their distance
 */
export const getBlend = createSelector(
  selectProfile,
  (profile) => profile.plusControl?.blend || 'optimal'
);

/**
 * Gets list of profile notifications
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Notifications} notifications
 */
export const getProfileNotifications = createSelector(
  selectProfile,
  (profile) => profile.notifications
);

/**
 * Gets the loading state for getting profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} fetching profile state
 */
export const getIsFetchingProfile = createSelector(
  selectProfile,
  (profile) => profile.state?.status === STATE_LOADING
);

/**
 * Gets the status for v2/profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} fetching profile status
 */
export const getFetchProfileStatus = createSelector(
  selectProfile,
  (profile) => profile.state?.status
);

// User
/**
 * Gets the current user from the profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {User} current user
 */
export const getCurrentUser = createSelector(selectProfile, (profile): User => profile.user);

/**
 * Gets the current user ID
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} user ID
 */
export const getProfileUserId = createSelector(getCurrentUser, (user) => user?._id);

/**
 * Gets the name of the current user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} user name
 */
export const getUserName = createSelector(getCurrentUser, (user) => user?.name || '');

/**
 * Gets the bio of the current user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} user Bio
 */
export const getUserBio = createSelector(getCurrentUser, (user) => user?.bio);

/**
 * Gets the birthday from profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} birthday
 */
export const getBirthday = createSelector(getCurrentUser, (user) => user?.birth_date);

export const getAutoExpansion = createSelector(getCurrentUser, (user) => user?.auto_expansion);

/**
 * Gets the calculated age from profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} age
 */
export const getAge = createSelector(getBirthday, calculateAge);

/**
 * Gets the gender from profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} 0 for male-presenting, 1 for female-presenting
 */
export const getGender = createSelector(getCurrentUser, (user) => user?.gender ?? GENDER_MALE);

/**
 * Gets the all inclusive gender from profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {AllInGender[]} new array of objects where we saved new genders
 */
export const getAllInGender = createSelector(
  getCurrentUser,
  (user) => user?.all_in_gender ?? emptyArray
);

/**
 * Gets the custom gender from profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} custom gender string
 */
export const getCustomGender = createSelector(getCurrentUser, (user) => user?.custom_gender);

/**
 * Gets whether the user is showing gender on their profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} true if the user is showing gender on their profile
 */
export const getShowGender = createSelector(
  getCurrentUser,
  (user) => user?.show_gender_on_profile ?? false
);

/**
 * Returns whether the user is male or set to appear in male searches
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} true if the user is male or appears in male searches
 */
export const getGenderIsMale = createSelector(getGender, genderIsMale);

/**
 * Returns whether the user is female or set to appear in female searches
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} true if the user is male or appears in female searches
 */
export const getGenderIsFemale = createSelector(getGender, genderIsFemale);

/**
 * Gets the orientations from profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<Orientation>} list of orientations represented as strings
 */
export const getOrientations = createSelector(
  getCurrentUser,
  (user) => user?.sexual_orientations || emptyArray
);

/**
 * Gets whether the user is showing their sexual orientations on their profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} true if the user is showing orientation on their profile
 */
export const getShowOrientation = createSelector(
  getCurrentUser,
  (user) => user?.show_orientation_on_profile ?? false
);

/**
 * Gets the list of schools on the user's profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<School>} user's schools
 */
export const getSchools = createSelector(getCurrentUser, (user) => user?.schools || emptyArray);

/**
 * Gets the list of jobs on the user's profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<Jobs>} user's jobs
 */
export const getJobs = createSelector(getCurrentUser, (user) => user?.jobs || emptyArray);

/**
 * Gets the list of photos on the user's profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<Photo>} user's photos
 */
export const getPhotos = createSelector(getCurrentUser, (user) => user?.photos || emptyArray);

/**
 * Gets the social metadata associated with the user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Social} user social metadata
 */
export const getSocial = createSelector(getCurrentUser, (user) => user?.social);

/**
 * Gets the mutual friends opt in status associated with the user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Boolean} user is opted into mutual friends feature
 */
export const getMutualFriendsOptInStatus = createSelector(
  getSocial,
  (social) => social?.mutuals_opt_in
);

/**
 * Gets the online status on the user's profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} user's online status
 */
export const getUserPresenceDisabled = createSelector(
  getCurrentUser,
  (user) => user?.user_presence_disabled ?? false
);

/**
 * Gets the first photo for a user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Photo} user's first photo
 */
export const getFirstPhoto = createSelector(getPhotos, (photos) => photos?.[0] || {});

/**
 * Gets the photos that were used for selfie verification
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<Photo>} users selfie verified photos
 */
export const getSelfieVerifiedPhotos = createSelector(getPhotos, (photos) =>
  photos.filter((photo) => Boolean(photo.selfie_verified))
);

/**
 * Gets whether the current user is discoverable
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the user is discoverable
 */
export const getIsDiscoverable = createSelector(
  getCurrentUser,
  (user) => user?.discoverable ?? true
);

/**
 * Gets whether the current user has smart photos enabled
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the user has smart photos enabled
 */
export const getIsSmartPhotosEnabled = createSelector(
  getCurrentUser,
  (user) => user?.photo_optimizer_enabled ?? false
);

/**
 * Gets the user's gender filter
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {GenderFilter} 0 for male-presenting, 1 for female-presenting, -1 for both gender
 */
export const getGenderFilter = createSelector(getCurrentUser, (user) => user?.gender_filter);

/**
 * Gets the user's gender all inclusive interested in genders
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {GenderFilter} array of 0 for male-presenting, 1 for female-presenting, 2 for beyond binary, -1 for all genders
 */
export const getInterestedInGenders = createSelector(
  getCurrentUser,
  (user) => user?.interested_in_genders ?? []
);

/**
 * Gets the user's preference for showing users of same sexual orientation first in discovery
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {?OrientationConfig}
 */
export const getShowSameOrientationFirstConfig = createSelector(
  getCurrentUser,
  (user) => user?.show_same_orientation_first
);

/**
 * Gets the user's minimum age filter
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} minimum age for recs
 */
export const getAgeMinFilter = createSelector(getCurrentUser, (user) => user?.age_filter_min);

export const getAgeAutoExpansionToggle = createSelector(
  getAutoExpansion,
  (autoExpansion) => autoExpansion?.age_toggle
);

/**
 * Gets the user's maximum age filter
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} maximum age for recs
 */
export const getAgeMaxFilter = createSelector(getCurrentUser, (user) => user?.age_filter_max);

/**
 * Gets the billing info of the current user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {BillingInfo} Billing Info
 */
export const getUserBillingInfo = createSelector(getCurrentUser, (user) => user?.billing_info);

// User Interests
export const getAvailableInterests = createSelector(
  getCurrentUser,
  (user) => user?.user_interests?.available_interests || emptyArray
);

export const getSelectedInterests = createSelector(
  getCurrentUser,
  (user) => user?.user_interests?.selected_interests || emptyArray
);

export const getInterestsRange = createSelector(getCurrentUser, (user) =>
  user
    ? {
        max: user.user_interests?.max_interests || 5,
        min: user.user_interests?.min_interests || 3,
      }
    : { max: 5, min: 3 }
);

/**
 * Gets the email address of the subscription
 *
 * @param  {Object} state - top-level state
 * @return {string} subscription email
 */
export const getWebSubscriptionEmail = createSelector(
  getUserBillingInfo,
  (billingInfo) => billingInfo?.email
);

/**
 * Gets the zip-code of the subscription
 *
 * @param  {Object} state - top-level state
 * @return {string} subscription zip-code
 */
export const getWebSubscriptionZipCode = createSelector(
  getUserBillingInfo,
  (billingInfo) => billingInfo?.zip_code || ''
);

/**
 * Gets the cpf status of user
 *
 * @param  {Object} state - top-level state
 * @return {boolean} if user has cpf on file
 */
export const getHasCpfOnFile = createSelector(
  getUserBillingInfo,
  (billingInfo) => billingInfo?.require_brazil_cpf ?? false
);

/**
 * Gets if zip-code is required during card update
 *
 * @param  {Object} state - top-level state
 * @return {boolean} zip code required
 */
export const getWebSubscriptionRequiresZip = createSelector(
  getWebSubscriptionZipCode,
  (zipCode) => !!zipCode
);

/**
 * Gets the user's maximum distance filter
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} maximum age for recs
 */
export const getDistanceFilter = createSelector(getCurrentUser, (user) => user?.distance_filter);

export const getDistanceAutoExpansionToggle = createSelector(
  getAutoExpansion,
  (autoExpansion) => autoExpansion?.distance_toggle
);

/**
 * Gets the user's phone number (id)
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} phone number of the user including country code
 */
export const getPhoneId = createSelector(getCurrentUser, (user) => user?.phone_id);

export const getCurrentUserHomeCity = createSelector(getCurrentUser, (user) => user?.city);

/**
 * Gets the user's username
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} username
 */
export const getUsername = createSelector(getCurrentUser, (user) => user?.username);

export const getIsGlobalModeEnabled = createSelector(
  getCurrentUser,
  (user) => user?.global_mode?.is_enabled
);

export const getGlobalModeLanguages = createSelector(
  getCurrentUser,
  (user) => user?.global_mode?.language_preferences || emptyArray
);

/**
 * Gets the user's position info
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Object} posInfo - Position info
 */
export const getCurrentUserPosInfo = createSelector(getCurrentUser, (user) => user?.pos_info || {});

/**
 * Gets the user's state's 2-letter abbreviation
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} stateCode - State/province code
 */
export const getStateAbbreviation = createSelector(
  getCurrentUserPosInfo,
  (posInfo) => posInfo?.state?.code
);

/**
 * Gets the user's country's 3-letter abbreviation
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} countryCode - Country code
 */
export const getCurrentUserCountryCode = createSelector(
  getCurrentUserPosInfo,
  (posInfo) => posInfo?.country?.alpha3
);

export const getStickers = (user) => user.bumper_stickers || [];
export const getEnabledSticker = (stickers: any) =>
  stickers?.find((sticker) => sticker.enabled, {});

export const getUserBumperStickerEnabled = createSelector(
  getCurrentUser,
  (user) => user?.bumper_sticker_enabled
);

export const getUserStickers = createSelector(getCurrentUser, getStickers);

export const getUserEnabledSticker = createSelector(
  getUserStickers,
  getUserBumperStickerEnabled,
  (stickers, bumperStickerEnabled) => (bumperStickerEnabled && getEnabledSticker(stickers)) || null
);

export const getProfileFacebookEmail = createSelector(
  selectProfile,
  (profile) => profile.account?.facebook_email
);

export const getProfilePercentAchieved = createSelector(
  selectProfile,
  (profile) => profile?.profileMeter?.percentAchieved ?? 0
);

export const selectProfileIncompleteComponent = createSelector(
  (state: GlobalStateType, key: string) => key,
  selectProfile,
  (key, profile) => profile?.profileMeter?.incompleteComponents?.find((obj) => obj.key === key)
);

// Travel
/**
 * Gets the travel state from the profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Travel} current user
 */
export const getTravel = createSelector(selectProfile, (profile) => profile.travel);

/**
 * Gets whether the current user is traveling (ie. using passport)
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the user is traveling
 */
export const getIsTraveling = createSelector(getTravel, (travel) => !!travel.is_traveling);

/**
 * Gets the current user's travel location
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Coordinates} travel location in x,y coorindates
 */
export const getTravelLocation = createSelector(getTravel, (travel) => travel.travel_pos);

/**
 * Gets the current user's travel location info
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {LocationInfo} travel location info
 */
export const getTravelLocationInfo = createSelector(
  getTravel,
  (travel) => travel.travel_location_info?.[0]
);

export const getAvailableDescriptors = createSelector(
  selectProfile,
  (profile) => profile.availableDescriptors || emptyArray
);

export const selectDescriptorsById = createSelector(getAvailableDescriptors, formatDescriptorsById);

export const getAllInGenderGroup = createSelector(
  selectProfile,
  (profile) => profile.allInGender || emptyArray
);

export const getSelectedDescriptors = createSelector(
  getCurrentUser,
  (user) => user?.selected_descriptors || emptyArray
);

export const getSelectedGenders = createSelector(
  getCurrentUser,
  (user) => user?.all_in_gender || emptyArray
);

export const getProfileHasRedactedEnabled = createSelector(
  getCurrentUser,
  (user) => user?.has_redacted_enabled || false
);

export const selectQuizzesSection = createSelector(
  getCurrentUser,
  (user): UserQuizzesSection | null => (user.sparks_quizzes ? user.sparks_quizzes[0] : null)
);

export const selectQuizById = createSelector(
  [selectQuizzesSection, (_state: GlobalStateType, quizId: UserQuizId): UserQuizId => quizId],
  (section, quizId): UserQuiz | null => section?.quizzes.find((quiz) => quiz.id === quizId) ?? null
);

export const selectDisplayGenders = createSelector(
  getCurrentUser,
  (user) => user.display_genders || emptyArray
);

export const selectDisplaySexualOrientations = createSelector(
  getCurrentUser,
  (user) => user.display_sexual_orientations || emptyArray
);
/**
 * This set of descriptors are displayed in the preview profile card.
 * This card is accessible to the users in /app/profile.
 * Only selectors SELECTOR_TYPE_SINGLE_SELECTION_SET, SELECTOR_TYPE_MULTI_SELECTION_SET and HEIGHT_MEASUREMENT_DESCRIPTOR
 * will be displayed.
 */
export const getSelectionSetDescriptorFiltered = (descriptors: Descriptor[] | undefined) =>
  descriptors
    ? descriptors.filter(
        (descriptor) =>
          descriptor.type === SELECTOR_TYPE_SINGLE_SELECTION_SET ||
          descriptor.type === SELECTOR_TYPE_MULTI_SELECTION_SET
      )
    : [];

/**
 * This set of descriptors are displayed in the preview profile card.
 * This card is accessible to the users in /app/profile.
 * Only selectors SELECTOR_TYPE_SINGLE_SELECTION_SET, SELECTOR_TYPE_MULTI_SELECTION_SET and HEIGHT_MEASUREMENT_DESCRIPTOR
 * will be displayed.
 */
export const getSelectionSetDescriptor = createSelector(
  getSelectedDescriptors,
  getSelectionSetDescriptorFiltered
);

export const getChoiceSelectorDescriptor = createSelector(getSelectedDescriptors, (descriptors) =>
  descriptors.filter((descriptor) => descriptor.type === SELECTOR_TYPE_CHOICE_SELECTOR_V1)
);

// Request Verification
export const getIsRequestVerificationEnabled = createSelector(
  getCurrentUser,
  (user) => user.request_verification_enabled ?? false
);

// User Interests
export const getAvailableUserInterests = createSelector(
  getCurrentUser,
  (user) => user.user_interests ?? emptyObject
);

// User Height Measure Type
export const getHeightMeasureType = createSelector(
  selectProfile,
  (profile) => profile.heightMeasureType
);

export const selectHasUserViewedAllInScreensBefore = createSelector(
  selectProfile,
  (profile) => profile.hasUserViewedAllInScreensBefore
);

// Create user local values
export const getUserLocalValues = createSelector(
  selectProfile,
  (profile) => profile.editAllInGenderLocalValues
);

export const getUserLocalDisplayGenders = createSelector(
  getUserLocalValues,
  (user) => user?.display_genders ?? []
);

export const getUserLocalDisplayOrientations = createSelector(
  getUserLocalValues,
  (user) => user?.display_sexual_orientations ?? []
);

export const selectUserPrompts = createSelector(getCurrentUser, (user) => user.user_prompts);

export const selectHasFacePhotos = createSelector(getCurrentUser, (user) =>
  Boolean(user?.has_face_photos)
);

// Feature Access
export const selectUserFeatureAccess = createSelector(
  selectProfile,
  (profile) => profile.featureAccess
);

// Feature Access
export const selectUserPreferenceFeatureAccess = createSelector(
  selectUserFeatureAccess,
  (featureAccess) => featureAccess?.preference_filters
);

// Feature Access
export const selectUserHasUnlimitedPreferencesAccess = createSelector(
  selectUserFeatureAccess,
  (featureAccess) => featureAccess?.preference_filters?.unlimited || false
);

// User Premium Preferences
export const selectUserPreferences = createSelector(
  getCurrentUser,
  (user) => user.preference_filters
);

// User Min Photo Preference
export const selectUserMinPhotosPreference = createSelector(
  selectUserPreferences,
  (preferences) => preferences?.preference_number_of_photos_filter
);

// User Has Bio Preference
export const selectUserHasBioPreference = createSelector(
  selectUserPreferences,
  (preferences) => preferences?.preference_has_bio_filter
);

// User Descriptor Preferences
export const selectUserPreferenceDescriptors = createSelector(
  selectUserPreferences,
  (preferences) => preferences?.preference_descriptors_filter
);
// User Interests Preferences
export const selectUserPreferenceInterests = createSelector(
  selectUserPreferences,
  (preferences) => preferences?.preference_user_interests_filter
);

// User Preferences - required tier (Plus Test)
export const selectUserPreferenceRequiredTier = createSelector(
  selectUserPreferenceFeatureAccess,
  (preferencesFeatureAccess) => preferencesFeatureAccess?.required_tier || PRODUCT_TYPE_GOLD
);

// User Preferences - allow list (Free Test)
export const selectUserPreferenceAllowList = createSelector(
  selectUserPreferenceFeatureAccess,
  (preferencesFeatureAccess) => preferencesFeatureAccess?.allow || []
);
